/**
 * W35-LAYERS-LAB — pure model for the Layers Lab index and the
 * per-game summary card. No React here: the chip mapping and the
 * summary assembly are pinned by unit tests, the components stay
 * thin.
 *
 * The payload contract is /api/v1/opsqa/matches (ops_qa_api.py,
 * opsqa_matches): status colors are computed SERVER-side from the
 * registry + bulk queries; this module only names, orders and
 * explains them — it never invents a color.
 */

export type ChipStatus = 'green' | 'yellow' | 'red'

export const LAYER_KEYS = [
  'l1', 'l2', 'l3', 'l4', 'l5', 'l6', 'serve',
] as const
export type LayerKey = (typeof LAYER_KEYS)[number]

export const LAYER_LABELS: Record<LayerKey, string> = {
  l1: 'L1', l2: 'L2', l3: 'L3', l4: 'L4', l5: 'L5', l6: 'L6',
  serve: 'Serve',
}

/** The chips' own labels: one character to spare the row's width (Ben,
 *  2026-10-17). Text and tooltips keep LAYER_LABELS ("Serve"). */
export const CHIP_LABELS: Record<LayerKey, string> = {
  ...LAYER_LABELS, serve: 'S',
}

export interface LayerChecks {
  clean: string[]
  failed: string[]
  /** analyses whose latest run is a refused build (tier insufficient) */
  not_measured?: string[]
  /** analyses whose latest run measured with a note (tier partial): built,
   *  and the reason said beside it, never a failed check */
  noted?: { analysis: string; reason: string | null }[]
  last_run_at: string | null
}

/** "signal-run (reason)" for each analysis measured with a note. */
function notedList(checks: LayerChecks | undefined): string {
  return (checks?.noted ?? []).map(
    (n) => n.reason ? `${n.analysis} (${n.reason})` : n.analysis).join(', ')
}

export interface LayerInfo {
  status: ChipStatus
  rows: number | null
  files?: number | null
  bytes?: number | null
  note?: string | null
  detail?: {
    expected?: string[]
    present?: string[]
    missing?: string[]
    counts?: Record<string, number>
    optional_absent?: string[]
    zero_legitimate?: string[]
    optional?: string[]
    checks?: LayerChecks
    batches?: number
    statuses?: string[]
    excluded_definitional?: string[]
  }
}

export interface HalftimeFlag {
  status: ChipStatus
  periods: number
  direction_flip: boolean | null
  note: string | null
}

export interface VideoQuality {
  label: string
  basis: string
  fps: number | null
  total_frames: number | null
  file_size_mb: number | null
  has_video: boolean
  video_frame_aligned: boolean | null
}

export interface ClubInfo {
  id: number
  name: string | null
  code: string | null
  logo_url: string | null
  color: string | null
}

export interface MatchRow {
  game_id: number
  game_code: string | null
  club: ClubInfo | null
  match_date: string | null
  home_team: string | null
  away_team: string | null
  vendor: string
  vendor_note: string | null
  video_quality: VideoQuality
  layers: Record<LayerKey, LayerInfo>
  halftime: HalftimeFlag
  any_red: boolean
  last_run_at: string | null
  last_artifact_at: number | null
  game_prefix: string | null
  pre_layers?: boolean
  has_r2_tracking?: boolean
  /** Deleted from public.games; its stack rows remain. */
  deleted?: boolean
  /** 'stack' only when fully converted: serve switch on under the
   *  environment's mode AND the video certificate built; else 'legacy'. */
  served_from?: 'stack' | 'legacy'
  /** Why, in words — the dot's tooltip. */
  served_note?: string
  /** Build steps whose most recent run failed (a build that did not
   *  finish); a later success clears a step. */
  failed_steps?: string[]
  /** Server's verdict: customers read this game from the stack while a
   *  build step is failed (stack-red). Drives "Red and served". */
  red_and_served?: boolean
  /** When a serve-* step last succeeded: when what readers see last
   *  changed. null when serve was never built. */
  serve_built_at?: string | null
  /** Each team's crest URL (away and home in the game code), null when
   *  there is none (ops_qa_api._attach_team_crests). */
  away_crest?: string | null
  home_crest?: string | null
  /** Match Lab's Human reviewed sign-off, current state
   *  (ops_qa_api._human_review_by_game); null when it could not be read. */
  human_reviewed?: { reviewed: boolean; by: string | null; at: string | null } | null
}

/** The index's Human reviewed dot and filter (Ben, 2026-10-22). 'unknown'
 *  where the server could not read the review table — never shown as
 *  "not reviewed", which would send someone to review it again. */
export type ReviewState = 'reviewed' | 'not_reviewed' | 'unknown'

export function reviewState(row: MatchRow): ReviewState {
  const h = row.human_reviewed
  if (h == null) return 'unknown'
  return h.reviewed ? 'reviewed' : 'not_reviewed'
}

export function reviewTooltip(row: MatchRow): string {
  const h = row.human_reviewed
  if (h == null) return 'Human review could not be read'
  const when = h.at ? new Date(h.at).toLocaleDateString(undefined,
    { year: 'numeric', month: 'short', day: 'numeric' }) : null
  const who = [h.by, when].filter(Boolean).join(', ')
  if (h.reviewed) return `Human reviewed${who ? ` (${who})` : ''}`
  return h.at ? `Not human reviewed — unset${who ? ` (${who})` : ''}`
    : 'Not human reviewed'
}

/** Where a match stands, for the index's status column and filter:
 *  - 'convert'  — no tracking ingested to the stack yet;
 *  - 'complete' — every layer and halftime green, no failed build step,
 *                 and served from the stack (switched on, video certified);
 *  - 'attention' — on the stack but something is not finished. */
export type MatchState = 'convert' | 'attention' | 'complete'

export const MATCH_STATE_LABELS: Record<MatchState, string> = {
  convert: 'Convert to stack',
  attention: 'Needs work',
  complete: 'Complete',
}

export function matchState(row: MatchRow): MatchState {
  if (row.pre_layers) return 'convert'
  const allGreen = LAYER_KEYS.every((k) => row.layers[k].status === 'green')
    && row.halftime.status === 'green'
  return allGreen && !(row.failed_steps?.length) && row.served_from === 'stack'
    ? 'complete' : 'attention'
}

/** Status sort order: needs work first, then to convert, then complete. */
export function stateRank(row: MatchRow): number {
  return { attention: 0, convert: 1, complete: 2 }[matchState(row)]
}

/** Why a match needs work, in words: the failed steps first (the build
 *  that did not finish), then the layers that are not green, then
 *  halftime and serving. Empty unless matchState is 'attention'. */
export function attentionReasons(row: MatchRow): string[] {
  if (matchState(row) !== 'attention') return []
  const out: string[] = []
  if (row.failed_steps?.length) out.push(`failed: ${row.failed_steps.join(', ')}`)
  const notGreen = LAYER_KEYS.filter((k) => row.layers[k].status !== 'green')
  if (notGreen.length) {
    out.push(`not green: ${notGreen.map((k) => LAYER_LABELS[k]).join(', ')}`)
  }
  if (row.halftime.status !== 'green') out.push('halftime')
  if (row.served_from !== 'stack') out.push('not served from stack')
  return out
}

/** Chip colors. Red marks a truly absent layer, per the ops palette. */
export const CHIP_COLORS: Record<ChipStatus, string> = {
  green: '#1c7c31',
  yellow: '#b28704',
  red: '#b3261e',
}

/** Tooltip for one layer chip: says WHY the color, naming the
 *  missing/yellow tables — never just restating the color. */
export function chipTooltip(key: LayerKey, info: LayerInfo): string {
  const label = LAYER_LABELS[key]
  const parts: string[] = []
  const d = info.detail
  if (info.status === 'green') {
    parts.push(`${label}: complete`)
    if (d?.present?.length) parts.push(`present: ${d.present.join(', ')}`)
  } else if (info.status === 'red') {
    parts.push(`${label}: nothing built`)
    if (d?.missing?.length) parts.push(`missing: ${d.missing.join(', ')}`)
  } else {
    parts.push(`${label}: partial`)
    if (d?.missing?.length) parts.push(`missing: ${d.missing.join(', ')}`)
  }
  if (d?.checks?.failed?.length) {
    parts.push(`failed checks: ${d.checks.failed.join(', ')}`)
  }
  if (d?.checks?.not_measured?.length) {
    parts.push(`not measured: ${d.checks.not_measured.join(', ')}`)
  }
  if (d?.checks?.noted?.length) {
    parts.push(`with a note: ${notedList(d.checks)}`)
  }
  if (d?.optional_absent?.length) {
    parts.push(`optional, absent: ${d.optional_absent.join(', ')}`)
  }
  if (d?.zero_legitimate?.length) {
    parts.push(
      `0 rows (may be legitimate): ${d.zero_legitimate.join(', ')}`)
  }
  if (info.note) parts.push(info.note)
  return parts.join(' — ')
}

/** Why a layer is not green, short enough to follow its section title
 *  ("L6 — derived signal — missing: …"). null when green. The same
 *  facts as chipTooltip, from the same server detail. */
export function notGreenReason(info: LayerInfo): string | null {
  if (info.status === 'green') return null
  const d = info.detail
  const parts: string[] = []
  if (info.status === 'red') parts.push('nothing built')
  else if (d?.missing?.length) parts.push(`missing: ${d.missing.join(', ')}`)
  const failed = d?.checks?.failed ?? []
  const notMeasured = d?.checks?.not_measured ?? []
  if (failed.length) parts.push(`failed checks: ${failed.join(', ')}`)
  if (notMeasured.length) parts.push(`not measured: ${notMeasured.join(', ')}`)
  if (!failed.length && !notMeasured.length && info.note) {
    parts.push(info.note)          // e.g. "built but no check record"
  }
  return parts.length ? parts.join('; ') : 'partial'
}

/** The Timings cell's words: null when the match periods are fine (the cell
 *  shows a check), else what is wrong in a few words — the server's own
 *  reasons (ops_qa_api._halftime_flag), shortened. */
export function timingsIssue(h: HalftimeFlag): string | null {
  if (h.status === 'green') return null
  if (h.periods === 0) return 'No match periods'
  const notes = (h.note ?? '').split(';').map((n) => n.trim()).filter(Boolean)
  const short = notes.map((n) =>
    n === 'fewer than 2 periods' ? `${h.periods} period${h.periods === 1 ? '' : 's'} only`
    : n === 'non-positive frame range' ? 'Bad frame range'
    : n === 'no clean_match_period rows' ? 'No match periods'
    : n)
  return short.join(', ') || 'Check timings'
}

export function halftimeTooltip(h: HalftimeFlag): string {
  const parts = [
    `Halftime: ${h.periods} period${h.periods === 1 ? '' : 's'}`,
    h.direction_flip === null
      ? 'attack direction measured per half by L3'
      : h.direction_flip
        ? 'attack direction flips between periods 1 and 2'
        : 'NO attack-direction flip',
  ]
  if (h.note) parts.push(h.note)
  return parts.join(' — ')
}

/** red < yellow < green, for worst-first sorting. */
export function statusRank(s: ChipStatus): number {
  return s === 'red' ? 0 : s === 'yellow' ? 1 : 2
}

/** The worst chip on the row (halftime included). */
export function worstRank(row: MatchRow): number {
  const ranks = LAYER_KEYS.map((k) => statusRank(row.layers[k].status))
  ranks.push(statusRank(row.halftime.status))
  return Math.min(...ranks)
}

/** Green chips on the row (the seven layers and halftime) — the index's
 *  "Layers" sort, most green first. */
export function greenCount(row: MatchRow): number {
  return LAYER_KEYS.filter((k) => row.layers[k].status === 'green').length
    + (row.halftime.status === 'green' ? 1 : 0)
}

/** 1 when the match is fully on the stack (the index's green dot). */
export function servedRank(row: MatchRow): number {
  return row.served_from === 'stack' ? 1 : 0
}

/** L1 and L2 both green: the layers a forced rebuild from L3 stands on
 *  (the server checks the same chips: opsqa_rebuild_from_l3). */
export function greenThroughL2(row: MatchRow): boolean {
  return row.layers.l1.status === 'green' && row.layers.l2.status === 'green'
}

export function anyRed(row: MatchRow): boolean {
  return worstRank(row) === 0
}

const fmtInt = (n: number) => n.toLocaleString('en-US')

const fmtBytes = (n: number | null | undefined): string =>
  n == null ? '—'
    : n >= 1_048_576 ? `${(n / 1_048_576).toFixed(1)} MB`
      : n >= 1024 ? `${(n / 1024).toFixed(1)} KB` : `${n} B`

/** One line per layer for the summary card: rows where the index
 *  measured them (Postgres layers), files+bytes where it could only
 *  stat (parquet layers) — and an honest "rows not counted here"
 *  never a zero. */
export function layerStatLine(_key: LayerKey, info: LayerInfo): string {
  const bits: string[] = []
  if (info.rows != null) bits.push(`${fmtInt(info.rows)} rows`)
  if (info.files != null && info.files > 0) {
    bits.push(`${info.files} file${info.files === 1 ? '' : 's'}`)
    if (info.bytes != null) bits.push(fmtBytes(info.bytes))
  }
  if (info.rows == null && !bits.length) {
    return info.status === 'red' ? 'nothing on disk' : 'present'
  }
  return bits.join(' · ')
}

export interface SummaryItem {
  label: string
  value: string
  title?: string
}

/** Build a concise "missing" summary across all layers + video +
 *  halftime for the second row in the index table.
 *  Returns null when nothing is missing (no second row needed). */
export function missingsSummary(row: MatchRow): string | null {
  const parts: string[] = []

  // Video
  if (!row.video_quality.has_video) {
    parts.push('video')
  }

  // Per-layer missing tables
  for (const k of LAYER_KEYS) {
    const d = row.layers[k].detail
    const missing = d?.missing ?? []
    if (missing.length > 0) {
      parts.push(`${LAYER_LABELS[k]} ${missing.join(', ')}`)
    }
  }

  // Halftime
  if (row.halftime.status === 'red') {
    parts.push('halftime periods')
  } else if (row.halftime.status === 'yellow' && row.halftime.note) {
    parts.push(`halftime: ${row.halftime.note}`)
  }

  const missing = parts.length > 0 ? `Missing: ${parts.join(' · ')}` : null
  // A build that did not finish, named first: it is what to re-run.
  const failed = row.failed_steps?.length
    ? `Failed build steps: ${row.failed_steps.join(', ')}` : null
  return [failed, missing].filter(Boolean).join(' — ') || null
}


/** "5m ago", "3h ago", "2d ago" — `ts` in epoch seconds or an ISO
 *  string; null when absent or unreadable. */
export function relativeTime(ts: string | number | null,
                             now: number = Date.now()): string | null {
  if (ts == null) return null
  const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts)
  if (isNaN(d.getTime())) return null
  const diff = Math.floor((now - d.getTime()) / 1000)
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

/** The facts the summary card shows above the fold, in order. */
export function summaryItems(row: MatchRow, now: number = Date.now()): SummaryItem[] {
  const items: SummaryItem[] = [
    { label: 'Vendor', value: row.vendor,
      ...(row.vendor_note ? { title: row.vendor_note } : {}) },
    { label: 'Video', value: row.video_quality.label,
      title: `Basis: ${row.video_quality.basis}` },
    { label: 'Halftime',
      value: row.halftime.status === 'green' ? 'ok'
        : row.halftime.status === 'red' ? 'absent' : 'suspect',
      title: halftimeTooltip(row.halftime) },
  ]
  const failed = LAYER_KEYS.flatMap(
    (k) => row.layers[k].detail?.checks?.failed ?? [])
  const notMeasured = LAYER_KEYS.flatMap(
    (k) => row.layers[k].detail?.checks?.not_measured ?? [])
  const noted = LAYER_KEYS.map((k) => notedList(row.layers[k].detail?.checks))
    .filter(Boolean)
  const notedCount = LAYER_KEYS.reduce(
    (n, k) => n + (row.layers[k].detail?.checks?.noted?.length ?? 0), 0)
  const clean = new Set(LAYER_KEYS.flatMap(
    (k) => row.layers[k].detail?.checks?.clean ?? []))
  // Failed first, then not measured, then with a note, each counted on
  // its own.
  const counted = [
    failed.length ? `${failed.length} FAILED` : null,
    notMeasured.length ? `${notMeasured.length} not measured` : null,
    notedCount ? `${notedCount} with a note` : null,
  ].filter(Boolean).join(', ')
  const named = [
    failed.length ? failed.join(', ') : null,
    notMeasured.length ? `not measured: ${notMeasured.join(', ')}` : null,
    noted.length ? `with a note: ${noted.join(', ')}` : null,
  ].filter(Boolean).join('; ')
  items.push({
    label: 'Checks',
    value: counted || (clean.size ? `${clean.size} clean` : 'none recorded'),
    ...(named ? { title: named } : {}),
  })
  if (row.last_run_at) {
    items.push({ label: 'Last run', value: row.last_run_at })
  }
  if (row.last_artifact_at) {
    // The newest file any build wrote under the game's stack (L2 and up).
    const at = new Date(row.last_artifact_at * 1000)
      .toISOString().replace('T', ' ').slice(0, 16) + ' UTC'
    items.push({
      label: 'Last artifact',
      value: `${at} (${relativeTime(row.last_artifact_at, now)})`,
      title: 'The newest file a build wrote for this game (L2 and up)',
    })
  }
  return items
}
